import { Box, Center, Flex, Stack, Text } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import { memo, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { pluralize } from '@/utils/helpers'

import type { ConversationUsersSeenConversationUserFragment } from '@/client/services/hasura/generated/graphql'

import ModalV3 from '@/components/UI/Modal/ModalV3'
import Tippy from '@/components/UI/Tippy'
import Avatar from '@/components/Worlds/UI/Avatar/Avatar'

const MAX_AVATARS_TO_SHOW = 5

function ConversationUsersSeenUnoptimized({
  conversationUsers,
  indexFromBottom,
}: {
  conversationUsers: ConversationUsersSeenConversationUserFragment[]
  indexFromBottom: number
}): React.ReactElement | null {
  const { ref, inView } = useInView()

  const [showModal, setShowModal] = useState(false)

  if (conversationUsers.length === 0) {
    return null
  }

  const enableModal = conversationUsers.length > 1
  const usersOverLimit = conversationUsers.length - MAX_AVATARS_TO_SHOW

  return (
    <>
      {enableModal ? (
        <ModalV3
          open={showModal}
          onClose={() => setShowModal(false)}
          title="Message seen by"
          body={() => (
            <Stack direction="column" width="100%" spacing="16px">
              {conversationUsers.map(({ user, last_read_at: lastReadAt }) => (
                <Stack direction="row" spacing="12px" align="center" key={`${user.id}`}>
                  <Avatar key={`${user.id}`} user={user} size="sm" />
                  <Box>
                    <Text fontWeight="bold">{user.virtual_display_name}</Text>
                    {lastReadAt == null ? null : (
                      <Text color="fg.subtle" variant="supporting">
                        Seen {DateTime.fromISO(lastReadAt).toFormat('MMMM d yyyy, h:mm a')}
                      </Text>
                    )}
                  </Box>
                </Stack>
              ))}
            </Stack>
          )}
        />
      ) : null}
      <Flex justify="flex-end" mt="4px" ref={ref}>
        {inView || indexFromBottom <= 10 ? (
          <Stack
            spacing="4px"
            direction="row"
            align="center"
            cursor={enableModal ? 'pointer' : 'default'}
            onClick={() => {
              if (!enableModal) {
                return
              }
              setShowModal(true)
            }}
          >
            {usersOverLimit > 0 ? (
              <Tippy
                content={`Seen by ${usersOverLimit} ${pluralize(usersOverLimit, 'other', {
                  unitsOnly: true,
                })}`}
              >
                <Center
                  borderRadius="8px"
                  bg="bg.muted"
                  height="16px"
                  minWidth="20px"
                  fontSize="10px"
                  color="fg.muted"
                  fontWeight="bold"
                >
                  +{usersOverLimit}
                </Center>
              </Tippy>
            ) : null}
            {conversationUsers
              .slice(0, MAX_AVATARS_TO_SHOW)
              .map(({ user, last_read_at: lastReadAt }) => (
                <Tippy
                  content={`Seen by ${user.virtual_display_name} at ${DateTime.fromISO(`${lastReadAt}`).toFormat('MMMM d yyyy, h:mm a')}`}
                  key={`${user.id}`}
                >
                  <Center>
                    <Avatar key={`${user.id}`} user={user} size="2xs" hideOnlineBadge />
                  </Center>
                </Tippy>
              ))}
          </Stack>
        ) : null}
      </Flex>
    </>
  )
}

const ConversationUsersSeen = memo(
  ConversationUsersSeenUnoptimized,
  (oldProps, newProps) => oldProps.conversationUsers.length === newProps.conversationUsers.length
)

export default ConversationUsersSeen
